import React, { useMemo, useState } from 'react';
import { Box, Grid, Typography, List, ListItem, ListItemButton } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#4F46E5', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

function SalesActionStatChartBox({ 
  statEvents = [], 
  selectedAction = '전체', 
  setSelectedAction,
  baseWorkMinutes, 
  userCount = 1 
}) {

  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr || !timeStr.includes(':')) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return (hours * 60) + minutes;
  };

  const { actionMenus, dynamicChartData } = useMemo(() => {
    const totalTeamBaseMinutes = (baseWorkMinutes || 0) * userCount;

    const totalCount = statEvents.reduce((acc, item) => acc + (Number(item.workCount) || 0), 0);
    const totalMinutesSum = statEvents.reduce((acc, item) => acc + parseTimeToMinutes(item.totalTime), 0);
    
    const chartDenominator = Math.max(totalTeamBaseMinutes, totalMinutesSum);

    const emptyMinutes = Math.max(0, totalTeamBaseMinutes - totalMinutesSum);
    const emptyPercentage = chartDenominator > 0 ? Number(((emptyMinutes / chartDenominator) * 100).toFixed(2)) : 0;

    const baseHours = Math.floor(totalTeamBaseMinutes / 60);
    const baseMins = totalTeamBaseMinutes % 60;
    const formattedBaseTime = `${String(baseHours).padStart(2, '0')}:${String(baseMins).padStart(2, '0')}`;

    const hours = Math.floor(totalMinutesSum / 60);
    const mins = totalMinutesSum % 60;
    const formattedTotalTime = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;

    const emptyHours = Math.floor(emptyMinutes / 60);
    const emptyMins = emptyMinutes % 60;
    const formattedEmptyTime = `${String(emptyHours).padStart(2, '0')}:${String(emptyMins).padStart(2, '0')}`;

    const overallPercentage = totalTeamBaseMinutes > 0 ? Number(((totalMinutesSum / totalTeamBaseMinutes) * 100).toFixed(2)) : 0;

    const menus = [
      { name: '전체', percentage: overallPercentage, workCount: totalCount, totalTime: formattedTotalTime, color: '#374151' },
      ...statEvents.map((item, index) => {
        const subMinutes = parseTimeToMinutes(item.totalTime);
        return {
          name: item.actionName,
          percentage: chartDenominator > 0 ? Number(((subMinutes / chartDenominator) * 100).toFixed(2)) : 0,
          workCount: item.workCount || 0,
          totalTime: item.totalTime || '00:00',
          color: COLORS[index % COLORS.length]
        };
      })
    ];

    if (emptyMinutes > 0) {
      menus.push({ name: '미활동', percentage: emptyPercentage, workCount: 0, totalTime: formattedEmptyTime, color: '#E5E7EB' });
    }

    const chartList = [];

    statEvents.forEach((item, index) => {
      const subMinutes = parseTimeToMinutes(item.totalTime);
      if (subMinutes > 0) {
        const isHighlighted = selectedAction === '전체' || selectedAction === item.actionName;
        const pct = chartDenominator > 0 ? Number(((subMinutes / chartDenominator) * 100).toFixed(2)) : 0;
        chartList.push({
          name: item.actionName,
          value: subMinutes,
          displayValue: pct,
          workCount: item.workCount || 0,
          totalTime: item.totalTime || '00:00',
          color: isHighlighted ? COLORS[index % COLORS.length] : '#E5E7EB'
        });
      }
    });

    if (emptyMinutes > 0) {
      const isHighlighted = selectedAction === '전체' || selectedAction === '미활동';
      chartList.push({
        name: '미활동',
        value: emptyMinutes,
        displayValue: emptyPercentage,
        workCount: 0,
        totalTime: formattedEmptyTime,
        color: isHighlighted ? '#F3F4F6' : '#E5E7EB'
      });
    }

    return { actionMenus: menus, dynamicChartData: chartList };
  }, [statEvents, selectedAction, baseWorkMinutes, userCount]); 

  const cardStyle = {
    p: 3, backgroundColor: '#ffffff', borderRadius: '12px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', border: '1px solid rgba(0, 0, 0, 0.03)',
    height: '100%', minHeight: 280, display: 'flex', flexDirection: 'column', boxSizing: 'border-box'
  };

  const renderTooltipContent = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box sx={{ backgroundColor: '#fff', p: 1.5, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: '600', mb: 0.5, color: '#111827' }}>{data.name}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
            <Typography variant="caption" color="text.secondary">비율:</Typography>
            <Typography variant="caption" sx={{ fontWeight: '600' }}>{data.displayValue}%</Typography>
          </Box>
          {data.name !== '미활동' && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
              <Typography variant="caption" color="text.secondary">건수:</Typography>
              <Typography variant="caption" sx={{ fontWeight: '600' }}>{data.workCount}건</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
            <Typography variant="caption" color="text.secondary">시간:</Typography>
            <Typography variant="caption" sx={{ fontWeight: '600' }}>{data.totalTime}</Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={cardStyle}>
      <Grid container spacing={2} alignItems="center" sx={{ height: '100%' }}>
        <Grid item xs={6.5}>
          <List disablePadding>
            {actionMenus.map((menu) => (
              <ListItem disablePadding key={menu.name}>
                <ListItemButton 
                  selected={selectedAction === menu.name}
                  onClick={() => setSelectedAction(menu.name)}
                  sx={{ 
                    py: 0.25, px: 1, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                    '&.Mui-selected': { backgroundColor: 'rgba(79, 70, 229, 0.08)', '&:hover': { backgroundColor: 'rgba(79, 70, 229, 0.12)' } }
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: menu.color, marginRight: 1.5, flexShrink: 0 }} />
                  <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Box sx={{ width: '135px', flexShrink: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: selectedAction === menu.name ? '600' : '400', color: selectedAction === menu.name ? '#4F46E5' : 'text.primary', whiteSpace: 'nowrap' }}>
                        {menu.name}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '35px', flexShrink: 0, textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ fontWeight: '500', color: selectedAction === menu.name ? '#4F46E5' : 'text.secondary' }}>
                        {menu.name === '미활동' ? '' : menu.workCount}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '25px', flexShrink: 0, textAlign: 'left', pl: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: '500', color: selectedAction === menu.name ? '#4F46E5' : 'text.secondary' }}>
                        {menu.name === '미활동' ? '' : '건'}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '60px', flexShrink: 0, textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ fontWeight: '500', color: selectedAction === menu.name ? '#4F46E5' : 'text.secondary' }}>
                        {menu.totalTime}
                      </Typography>
                    </Box>
                    <Box sx={{ width: '60px', flexShrink: 0, textAlign: 'right', pr: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: '600', color: selectedAction === menu.name ? '#4F46E5' : 'text.primary' }}>
                        {menu.percentage}%
                      </Typography>
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Grid>
        <Grid item xs={5.5}>
          <Box sx={{ width: '125%', height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: '-15%' }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: '600', color: 'text.secondary', letterSpacing: '-0.3px' }}>
              활동 비율 통계 ({userCount}명 기준)
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              {dynamicChartData.length > 0 ? (
                <PieChart>
                  <Tooltip content={renderTooltipContent} />
                  <Pie data={dynamicChartData} cx="50%" cy="45%" innerRadius={70} outerRadius={115} paddingAngle={2.5} dataKey="value" label={false} startAngle={90} endAngle={-270}>
                    {dynamicChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                  </Pie>
                </PieChart>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography variant="caption" color="text.secondary">조회된 통계 데이터가 없습니다.</Typography>
                </Box>
              )}
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default SalesActionStatChartBox;
